import { onMounted, onUnmounted } from 'vue'

/**
 * Page- or shell-level hotkey registration for the next UI.
 *
 * Lives under next/ because it's new code that the legacy shell does
 * not consume — keeping it here avoids accidentally pulling shortcut
 * behavior into legacy via cross-import.
 *
 * Rules baked in:
 *   - Single-character keys only fire when the event target is not
 *     an <input> / <textarea> / contenteditable element, unless the
 *     binding opts in via ``allowInInputs: true``.
 *   - Modifier combos (meta/ctrl + key) always fire — including from
 *     inputs — so things like ⌘+Enter work while editing.
 *   - Hotkey keys are normalized: lowercased single chars, plus the
 *     literal tokens "enter", "escape", "\\".
 *
 * Binding string format: ``[meta+][ctrl+][shift+][alt+]<key>``.
 *   Examples: "meta+k", "ctrl+enter", "\\", "a", "escape", "shift+?".
 */

export interface Hotkey {
  /** Binding string. See file header for the format. */
  key: string
  /** Called when the binding matches. The original event is passed. */
  handler: (e: KeyboardEvent) => void
  /** Set to ``true`` to fire even when typing in an input / textarea. */
  allowInInputs?: boolean
  description?: string
  /** Show in the always-visible footer bar, not just the help dialog. */
  primary?: boolean
  /** Group heading in the help dialog. */
  group?: string
  /** Hide entirely from the bar and dialog (e.g. the key that opens the dialog). */
  hidden?: boolean
}

interface ParsedBinding {
  key: string
  meta: boolean
  ctrl: boolean
  shift: boolean
  alt: boolean
}

export const IS_MAC = typeof navigator !== 'undefined'
  && /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent || '')

function parseBinding(binding: string): ParsedBinding {
  const parts = binding.toLowerCase().split('+').map((p) => p.trim()).filter(Boolean)
  const flags = { meta: false, ctrl: false, shift: false, alt: false }
  let key = ''
  for (const p of parts) {
    if (p === 'mod') {
      if (IS_MAC) flags.meta = true
      else flags.ctrl = true
    }
    else if (p === 'meta' || p === 'cmd') flags.meta = true
    else if (p === 'ctrl') flags.ctrl = true
    else if (p === 'shift') flags.shift = true
    else if (p === 'alt' || p === 'option') flags.alt = true
    else key = p
  }
  return { key, ...flags }
}

export interface KeyCap {
  text: string
  /** True for ⌘ ⌥ ⌃ ⇧ ⌫ — these need .kbdGlyph's optical size bump. */
  glyph?: boolean
}

export function formatBinding(binding: string): KeyCap[] {
  const parts = binding.toLowerCase().split('+').map((p) => p.trim()).filter(Boolean)
  const out: KeyCap[] = []
  for (const p of parts) {
    if (p === 'mod') out.push(IS_MAC ? { text: '⌘', glyph: true } : { text: 'Ctrl' })
    else if (p === 'meta' || p === 'cmd') out.push({ text: '⌘', glyph: true })
    else if (p === 'ctrl') out.push(IS_MAC ? { text: '⌃', glyph: true } : { text: 'Ctrl' })
    else if (p === 'shift') out.push({ text: 'Shift' })
    else if (p === 'alt' || p === 'option') out.push(IS_MAC ? { text: '⌥', glyph: true } : { text: 'Alt' })
    else if (p === 'enter') out.push({ text: 'Enter' })
    else if (p === 'escape') out.push({ text: 'Esc' })
    else if (p === 'delete') out.push({ text: IS_MAC ? 'Delete' : 'Del' })
    else if (p === 'space') out.push({ text: 'Space' })
    else if (p === 'arrowup') out.push({ text: '↑', glyph: true })
    else if (p === 'arrowdown') out.push({ text: '↓', glyph: true })
    else if (p === 'arrowleft') out.push({ text: '←', glyph: true })
    else if (p === 'arrowright') out.push({ text: '→', glyph: true })
    else out.push({ text: p.length === 1 ? p.toUpperCase() : p })
  }
  return out
}

export function formatBindingLabel(binding: string): string {
  return formatBinding(binding).map((c) => c.text).join(IS_MAC ? '' : '+')
}

export function matchesBinding(e: KeyboardEvent, binding: string): boolean {
  const p = parseBinding(binding)
  return p.key === eventKey(e)
    && p.meta === e.metaKey
    && p.ctrl === e.ctrlKey
    && p.shift === e.shiftKey
    && p.alt === e.altKey
}

function eventKey(e: KeyboardEvent): string {
  // Vue/dom KeyboardEvent.key: "a", "Enter", "Escape", "\\", " " (space).
  const raw = e.key
  if (raw === ' ') return 'space'
  // The Mac key labelled "delete" reports Backspace
  if (raw === 'Backspace') return 'delete'
  return raw.toLowerCase()
}

/**
 * Inputs that swallow single-character hotkeys because the user is typing text
 * into them.
 *
 * Deliberately excludes non-text input types. A checkbox or radio is an
 * ``<input>``, but typing "j" into one does nothing, so treating it as a typing
 * target silently kills list navigation whenever a row checkbox holds focus.
 */
const TEXT_INPUT_TYPES = new Set([
  'text', 'search', 'email', 'password', 'tel', 'url', 'number',
  'date', 'datetime-local', 'month', 'time', 'week',
])

export function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false
  if (t.isContentEditable) return true
  if (t.tagName === 'TEXTAREA') return true
  if (t.tagName === 'INPUT') {
    // Missing/unknown type defaults to text, per the HTML spec.
    const type = (t as HTMLInputElement).type?.toLowerCase() || 'text'
    return TEXT_INPUT_TYPES.has(type)
  }
  return false
}

/**
 * Register one or more hotkeys for the lifetime of the calling
 * component. Returns a cleanup function — useful if a component
 * needs to deregister before unmount, but the unmount hook will
 * call it automatically.
 */
export function useHotkeys(bindings: Hotkey[]): () => void {
  const parsed = bindings.map((b) => ({ ...b, parsed: parseBinding(b.key) }))

  function onKey(e: KeyboardEvent) {
    const k = eventKey(e)
    const typing = isTypingTarget(e.target)
    for (const b of parsed) {
      const p = b.parsed
      if (p.key !== k) continue
      if (p.meta !== e.metaKey) continue
      if (p.ctrl !== e.ctrlKey) continue
      if (p.shift !== e.shiftKey) continue
      if (p.alt !== e.altKey) continue
      // Single-char keys (no modifier) suppress in input-like targets
      // unless explicitly allowed.
      const hasModifier = p.meta || p.ctrl || p.alt
      if (typing && !hasModifier && !b.allowInInputs) continue
      b.handler(e)
      return
    }
  }

  function attach() { window.addEventListener('keydown', onKey) }
  function detach() { window.removeEventListener('keydown', onKey) }

  onMounted(attach)
  onUnmounted(detach)

  return detach
}
